import { fetchApiJson, trackError, trackUserEvent } from '@vend/utilities'
import { ApiCustomer } from '@vend/types'
import { add } from 'vend-number'
import { LineItem } from '../../models/line-item'
import { SaleDiscount } from '../../models/sale-discount'
import { SaleTax } from '../../models/sale'
import { getPromotionSummary } from '../sale'

export type InvoicingQuotePromotion = {
  promotionId: string
  name: string
  amount: string
  description: string
  promoCode: string
}

export type InvoicingQuoteTaxComponent = {
  rateId: string
  name: string
  rate: number
  amount: string
}

export type InvoicingQuoteLineDiscount = {
  percent: number
  amount: string
  id: string
  name: string
}

export type InvoicingQuoteLineItem = {
  productName: string
  sku: string
  quantity: number
  basePrice: string
  unitPrice: string
  finalUnitPrice: string // New API field: tells the sale the item's final price.
  unitTax: string // New API field: tells the sale the exact tax for one item.
  taxRate: number
  discountPercent: number
  discount?: InvoicingQuoteLineDiscount // New API field: keeps item discounts on the sale.
  note: string
  metadata: Record<string, string>
  lineSubtotal: string
  taxAmount: string
  lineTotal: string
  taxes: InvoicingQuoteTaxComponent[]
}

export type InvoicingQuotePayload = {
  source: string
  pli: string
  lineItems: InvoicingQuoteLineItem[]
  customer: {
    name: string
    email: string
    metadata?: Record<string, string>
  }
  headerNote: string
  internalNote?: string
  saleDiscount: {
    amount: string
    isPercentage: boolean
    metadata?: Record<string, string> // New API data: lets the sale recreate the discount.
  } | null
  discountTotal: string
  subtotal: string
  taxTotal: string
  taxes: InvoicingQuoteTaxComponent[]
  promotions: InvoicingQuotePromotion[]
  promotionsTotal: string
  total: string
  currency: string
  companyName: string
  taxLabel: string
  projectTitle: string
  metadata: Record<string, string>
}

export type CreateInvoicingQuoteResponse = {
  success: boolean
  quoteId?: string
  error?: string
}

/**
 * Maps sale line items to the invoicing quote payload format.
 */
export const mapLineItems = (
  lineItems: LineItem[]
): InvoicingQuoteLineItem[] => {
  return lineItems.map(lineItem => {
    const discountPercent = Number(lineItem.displayDiscountPercent) || 0
    const basePrice = Number(lineItem.basePrice) || 0
    const price = Number(lineItem.price) || 0
    const isMarkup = price > basePrice
    const discountAmount = !isMarkup
      ? String(lineItem.displayDiscount || 0)
      : '0'
    const promotion = lineItem.promotionDetails?.[0]

    const rawTaxComponents = lineItem.taxComponents || []
    const taxComponents: InvoicingQuoteTaxComponent[] = rawTaxComponents.map(
      component => ({
        rateId: component.rate_id || component.rate?.id || '',
        name: component.rate?.display_name || component.rate?.name || '',
        rate: component.rate?.rate || 0,
        amount: String(component.total_tax),
      })
    )

    const taxAmount = lineItem.totals?.tax || '0'
    const lineSubtotal = lineItem.totals?.exclusivePrice || '0'

    return {
      productName:
        lineItem.product?.variant_name || lineItem.product?.name || '',
      sku: lineItem.product?.sku || '',
      quantity: Number(lineItem.quantity),
      basePrice: String(lineItem.basePrice),
      unitPrice: String(isMarkup ? lineItem.price : lineItem.basePrice),
      finalUnitPrice: String(lineItem.price),
      unitTax: String(lineItem.tax || '0'),
      taxRate: lineItem.taxInfo?.rates?.[0]?.rate || 0,
      discountPercent: isMarkup ? 0 : discountPercent,
      ...(!isMarkup && Number(discountAmount) > 0
        ? {
            discount: {
              percent: discountPercent,
              amount: discountAmount,
              id: promotion?.id || '',
              name: promotion?.name || '',
            },
          }
        : {}),
      note: lineItem.note || '',
      metadata: {
        productId: lineItem.product?.id || '',
        lineItemLocalId: lineItem.localId,
        taxId: lineItem.taxInfo?.id || '',
      },
      lineSubtotal: String(lineSubtotal),
      taxAmount: String(taxAmount),
      lineTotal: String(add(lineSubtotal, taxAmount)),
      taxes: taxComponents,
    }
  })
}

const mapSaleTaxComponents = (
  taxes: Record<string, SaleTax> | undefined
): InvoicingQuoteTaxComponent[] =>
  Object.entries(taxes || {}).map(([taxId, tax]) => ({
    rateId: taxId,
    name: tax.name,
    rate: tax.rate?.rate || 0,
    amount: String(tax.total),
  }))

/**
 * Builds the full payload for the invoicing quote API.
 */
export const buildQuotePayload = ({
  retailerId,
  discountProductId,
  outletId,
  registerId,
  userId,
  lineItems,
  customer,
  saleNote,
  printNoteOnReceipt,
  discount,
  displayDiscount,
  discountIsPercentage,
  currency,
  companyName,
  subtotal,
  totalTax,
  taxes,
  total,
}: {
  retailerId: string | undefined
  discountProductId: string | undefined
  outletId: string | undefined
  registerId: string | undefined
  userId: string | undefined
  lineItems: LineItem[]
  customer: ApiCustomer | null | undefined
  saleNote: string | undefined
  printNoteOnReceipt: boolean
  discount: SaleDiscount | null | undefined
  displayDiscount: number | string
  discountIsPercentage: boolean
  currency: string | undefined
  companyName: string | undefined
  subtotal: number | string
  totalTax: number | string
  taxes: Record<string, SaleTax> | undefined
  total: number | string
}): InvoicingQuotePayload => {
  const hasSaleDiscount = discount && parseFloat(String(discount.price)) !== 0
  const promotionSummary = getPromotionSummary(lineItems)
  const totalPromotionAmount = promotionSummary.reduce(
    (sum, promo) => add(sum, promo.total || 0),
    0
  )
  const saleDiscountMetadata =
    hasSaleDiscount &&
    (discount?.id || discount?.taxInfo?.id || discount?.taxComponents?.length)
      ? {
          sale_discount: JSON.stringify({
            discount_product_id: discountProductId || '', // Use the retailer's configured product so the sale can be created.
            amount: Number(discount?.price ?? 0),
            tax: Number(discount?.tax ?? 0),
            tax_id: discount?.taxInfo?.id || '',
            tax_components: (discount?.taxComponents || []).map(component => ({
              rate_id: component.rate_id || '',
              total: Number(component.total_tax || 0),
            })),
          }),
        }
      : undefined

  return {
    source: 'x_series_sale',
    pli: `lsx:${outletId}` || '',
    lineItems: mapLineItems(lineItems),
    customer: {
      name: customer
        ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
        : '',
      email: customer?.email || '',
      metadata: {
        customerId: customer?.id || '',
        customerGroupId: customer?.customer_group_id || '',
      },
    },
    headerNote: printNoteOnReceipt ? saleNote || '' : '',
    internalNote: !printNoteOnReceipt ? saleNote || '' : undefined,
    saleDiscount: hasSaleDiscount
      ? {
          amount: String(displayDiscount),
          isPercentage: discountIsPercentage,
          metadata: saleDiscountMetadata,
        }
      : null,
    discountTotal: hasSaleDiscount ? String(discount?.price ?? 0) : '0',
    subtotal: String(subtotal),
    taxTotal: String(totalTax),
    taxes: mapSaleTaxComponents(taxes),
    promotions: promotionSummary.map(promo => ({
      promotionId: promo.id || '',
      name: promo.name || '',
      amount: String(promo.total || 0),
      description: promo.description || '',
      promoCode: promo.promo_code || '',
    })),
    promotionsTotal: String(totalPromotionAmount),
    total: String(total),
    currency: currency || '',
    companyName: companyName || '',
    taxLabel: lineItems[0]?.taxInfo?.display_name || '',
    projectTitle: '',
    metadata: {
      registerId: registerId || '',
      userId: userId || '',
    },
  }
}

/**
 * Creates a quote via the invoicing quote service proxy.
 *
 * @param payload The quote payload to send
 * @param idempotencyKey A unique key to prevent duplicate quote creation
 * @returns The created quote ID on success, or an error
 */
export const createInvoicingQuote = async (
  payload: InvoicingQuotePayload,
  idempotencyKey: string,
  tenantRef: string
): Promise<CreateInvoicingQuoteResponse> => {
  try {
    const response = await fetchApiJson(
      '/api/2.0/invoicing-quotes/v1/quotes/populated',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Ref': tenantRef,
        },
        body: JSON.stringify(payload),
      }
    )

    if (response.error || !response.id) {
      const errorMessage =
        response.message || response.error || 'Failed to create quote.'
      trackError('Invoicing Quote Creation Error', errorMessage, {
        source: payload.source,
        tenantRef,
        responseBody: JSON.stringify(response),
        lineItems: JSON.stringify(payload.lineItems),
      })
      return {
        success: false,
        error: errorMessage,
      }
    }

    trackUserEvent('Quotes', 'Invoicing Quote Created', {
      quoteId: response.id,
      source: payload.source,
      lineItemCount: payload.lineItems.length,
    })

    return {
      success: true,
      quoteId: response.id,
    }
  } catch (err: any) {
    const errorMessage = err?.message || 'Unknown error occurred'
    trackError('Invoicing Quote Creation Error', errorMessage, {
      source: payload.source,
      tenantRef,
      lineItems: JSON.stringify(payload.lineItems),
    })

    return {
      success: false,
      error: errorMessage,
    }
  }
}
